import { FormGroup } from '@angular/forms';

/** Represents one portion of a cron experession (part 1, 2, 3 ,4, or 5 of `* * * * *`) */
export interface CronPart {
    /** If true, this cron part uses the "/" for skip syntax */
    isSkip?: boolean;
    /** If true, this cron part is numerical */
    isNumber?: boolean;
    /** If true, this cron part is range (with dashes) or list (with commas) */
    isList?: boolean;
    /** If true, this cron part is the letter L (meaning last day of week or month) */
    isLast?: boolean;
    /** If true, this cron part is just a star */
    isStar?: boolean;
    /** The value of this cron part */
    value?: string;
}

/** Models the expected cron expression format for each of our plain-english scheduling forms */
export const FrequencyCronPatterns: { [key: string]: Array<Array<CronPart>> } = {
    minute: [
        [{ isSkip: true }, { isStar: true }, { isStar: true }, { isStar: true }, { isStar: true }],
        [{ isStar: true }, { isStar: true }, { isStar: true }, { isStar: true }, { isStar: true }]
    ],
    hour: [
        [{ isNumber: true }, { isSkip: true }, { isStar: true }, { isStar: true }, { isStar: true }],
        [{ isNumber: true }, { isStar: true }, { isStar: true }, { isStar: true }, { isStar: true }]
    ],
    day: [
        [{ isNumber: true }, { isNumber: true }, { isSkip: true }, { isStar: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isStar: true }, { isStar: true }, { isStar: true }]
    ],
    week: [
        [{ isNumber: true }, { isNumber: true }, { isStar: true }, { isStar: true }, { isNumber: true }],
        [{ isNumber: true }, { isNumber: true }, { isStar: true }, { isStar: true }, { isList: true }]
    ],
    month: [
        [{ isNumber: true }, { isNumber: true }, { isNumber: true }, { isSkip: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isNumber: true }, { isStar: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isList: true }, { isSkip: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isList: true }, { isStar: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isLast: true }, { isSkip: true }, { isStar: true }],
        [{ isNumber: true }, { isNumber: true }, { isLast: true }, { isStar: true }, { isStar: true }]
    ]
};

/**
 * The options for cadence frequency
 */
export enum Frequency {
    min = 'minute',
    hour = 'hour',
    day = 'day',
    week = 'week',
    month = 'month',
    custom = 'custom'
}

/**
 * The options for cadence frequency bit mask
 */
export class FrequencyBitMask {
    static min = 0b00_001;
    static hour = 0b00_010;
    static day = 0b00_100;
    static week = 0b01_000;
    static month = 0b10_000;
    static custom = 0b00_000;
}

/**
 * Cadence frequency options with additional metadata for the UI
 */
export const FrequencyOptions = [
    {
        id: Frequency.day,
        mask: FrequencyBitMask.day,
        description: 'Every [x] day(s) at [h:mm]',
        name: 'Daily',
        helpText: 'Daily, or every [x] days',
        displayType: 'day(s)'
    },
    {
        id: Frequency.week,
        mask: FrequencyBitMask.week,
        description: 'Every week on [Sun-Sat] at [h:mm]',
        name: 'Weekly',
        helpText: 'Weekly, on a particular day of the week',
        displayType: 'week'
    },
    {
        id: Frequency.month,
        mask: FrequencyBitMask.month,
        description: 'Every [x] month(s) on the [1st-31st] at [h:mm]',
        name: 'Monthly',
        helpText: 'Monthly, or every [x] months, on a particular day of the month',
        displayType: 'month(s)'
    },
    {
        id: Frequency.hour,
        mask: FrequencyBitMask.hour,
        description: 'Every [x] hour(s) at [mm] minutes past the hour',
        name: 'Hourly',
        helpText: 'Hourly, or every [x] hours',
        displayType: 'hour(s)'
    },
    {
        id: Frequency.min,
        mask: FrequencyBitMask.min,
        description: 'Every [x] minute(s)',
        name: 'By minute',
        helpText: '',
        displayType: 'minute(s)'
    },
    {
        id: Frequency.custom,
        mask: FrequencyBitMask.custom,
        description: 'Enter a raw cron expression',
        name: 'Custom',
        helpText: '',
        displayType: 'custom'
    }
];

export enum Meridian {
    am = 'am',
    pm = 'pm'
}

export interface CronEvent {
    rawCron: string;
    controlName: string;
    formGroup: FormGroup;
}
